import React, { useState, useRef, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Drawer, useTheme, alpha } from '@mui/material';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import NavJumpDialog from './NavJumpDialog';
import { useTheme as useThemeMode } from '../../context/ThemeContext';
import { DARK_TONES } from '../../theme/refinedDark';
import AnnouncementBanner from '../common/AnnouncementBanner';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import WelcomeTour from '../onboarding/WelcomeTour';

const DRAWER_WIDTH = 240;
const MINI_DRAWER_WIDTH = 64;

const MainLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [jumpOpen, setJumpOpen] = useState(false);
  const [bannerHeight, setBannerHeight] = useState(0);
  const bannerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const { darkTone } = useThemeMode();
  const announcements = useAnnouncements();

  useEffect(() => {
    const updateBannerHeight = () => {
      if (bannerRef.current) {
        setBannerHeight(bannerRef.current.offsetHeight);
      } else {
        setBannerHeight(0);
      }
    };

    updateBannerHeight();

    const observer = new ResizeObserver(updateBannerHeight);
    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => observer.disconnect();
  }, [announcements]);

  // ⌘K / Ctrl K opens the page search from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setJumpOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const currentDrawerWidth = drawerOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH;
  const topOffset = 56 + bannerHeight; // Compact AppBar height + banner height

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Announcement Banner */}
      {announcements.length > 0 && (
        <Box
          ref={bannerRef}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
          }}
        >
          <AnnouncementBanner announcements={announcements} />
        </Box>
      )}

      {/* Top App Bar */}
      <TopBar
        drawerWidth={currentDrawerWidth}
        onMenuClick={handleDrawerToggle}
        onDesktopMenuClick={handleDesktopDrawerToggle}
        isSidebarCollapsed={!drawerOpen}
        topOffset={bannerHeight}
      />

      {/* Navigation Drawer */}
      <Box
        component="nav"
        data-tour="sidebar"
        sx={{
          width: { lg: currentDrawerWidth },
          flexShrink: { lg: 0 },
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              mt: `${bannerHeight}px`,
              height: `calc(100% - ${bannerHeight}px)`,
              border: 'none',
              transition: theme.transitions.create(['margin-top', 'height'], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.standard,
              }),
            },
          }}
        >
          <Sidebar
            onItemClick={() => setMobileOpen(false)}
            onSearch={() => { setMobileOpen(false); setJumpOpen(true); }}
            collapsed={false}
          />
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              mt: `${bannerHeight}px`,
              height: `calc(100% - ${bannerHeight}px)`,
              border: 'none',
              transition: theme.transitions.create(
                ['width', 'margin-top', 'height'],
                {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.standard,
                }
              ),
            },
          }}
          open
        >
          <Sidebar collapsed={!drawerOpen} onSearch={() => setJumpOpen(true)} />
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 2.5 },
          width: { lg: `calc(100% - ${currentDrawerWidth}px)` },
          mt: `${topOffset}px`,
          minHeight: `calc(100vh - ${topOffset}px)`,
          backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : alpha(theme.palette.primary.main, 0.02),
          // The dark canvas's faint light at the top.
          backgroundImage: theme.palette.mode === 'dark' ? DARK_TONES[darkTone].glow : 'none',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
          // Doodle pattern only in light mode — in dark mode the pattern
          // contrasts loudly against the dark background and clutters the
          // content. Setting `display: 'none'` keeps the rule tidy without
          // duplicating the whole &::before block.
          '&::before': theme.palette.mode === 'dark'
            ? { display: 'none' }
            : {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: 'url(/images/doodle-1.png)',
                backgroundSize: '200px auto',
                backgroundRepeat: 'repeat',
                opacity: 0.15,
                zIndex: 0,
              },
          transition: theme.transitions.create(
            ['width', 'margin-top', 'min-height'],
            {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }
          ),
        }}
      >
        <Box data-tour="main-content" sx={{ position: 'relative', zIndex: 1 }}>
          <Outlet />
        </Box>
      </Box>

      <NavJumpDialog open={jumpOpen} onClose={() => setJumpOpen(false)} />

      {/* Onboarding tour — self-gated to school admins on first login. */}
      <WelcomeTour />
    </Box>
  );
};

export default MainLayout;
